/**
 * Structured client-side event capture. No-op (and no posthog-js download) when
 * NEXT_PUBLIC_POSTHOG_KEY is unset, mirroring PostHogProvider's opt-in guard.
 * posthog-js is a singleton: once the provider calls init(), this dynamic import
 * resolves to the same initialized instance. The key is inlined into the client
 * bundle by Next at build time, so the guard runs without a network round-trip.
 */
export type OutboundLinkType = "map" | "website" | "social_menu" | "source";

export interface OutboundLinkEvent {
  link_type: OutboundLinkType;
  destination_url?: string;
  venue_id?: string;
  venue_slug?: string;
  venue_name?: string;
  city_slug?: string;
  state_slug?: string;
  source_kind?: string;
}

export function captureOutbound(event: OutboundLinkEvent): void {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.capture("outbound_link_clicked", event);
  });
}

export interface SubmissionFailedEvent {
  target_type: string;
  reason: "network_error" | "http_error" | "timeout";
  form?: string;
  status?: number;
  message?: string;
}

/**
 * The submit forms' fetch is wrapped in try/catch, so a failure is a *caught* error —
 * Sentry's automatic instrumentation and PostHog's capture_exceptions (both uncaught-only)
 * never see it. This is the only place that failure gets recorded anywhere.
 */
export function captureSubmissionFailed(
  event: SubmissionFailedEvent,
  error?: unknown,
): void {
  console.error("[submit] submission failed", event, error);
  void import("@/instrumentation-client").then(({ captureClientException }) => {
    captureClientException(error ?? new Error(`submission_failed: ${event.reason}`), {
      ...event,
    });
  });
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.capture("submission_failed", event);
  });
}

export interface SubmissionBlockedEvent {
  form: string;
  reason: string;
  message?: string;
  /** Which fields had something typed in them (names only — values go to our own DB). */
  filled_fields?: string[];
}

/**
 * A submit form stopped the visitor before (or instead of) a successful POST. Mirrors
 * the row written to submission_failures so the funnel shows up in PostHog too.
 */
export function captureSubmissionBlocked(event: SubmissionBlockedEvent): void {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.capture("submission_blocked", event);
  });
}

export type LocationRequestPage = "landing" | "city";
export type LocationRequestOutcome =
  | "granted"
  | "denied"
  | "unavailable"
  | "timeout"
  | "no_city_match";

export interface LocationRequestedEvent {
  page: LocationRequestPage;
  outcome: LocationRequestOutcome;
  /** "landing_handoff" = the city table auto-located after a landing-page click (not a second tap). */
  source?: "click" | "landing_handoff";
}

/** One event per "Use my location" request, fired when the browser answers (never with coords). */
export function captureLocationRequested(event: LocationRequestedEvent): void {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.capture("location_requested", event);
  });
}
